'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ShopLayout } from '@/components/shop/ShopLayout'
import { useAuth } from '@/context/AuthContext'
import { getMyOrders, OrderSummary } from '@/lib/api'
import { formatPrice, formatDate, orderStatusLabel } from '@/lib/format'

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  shipped: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  cancelled: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
}

export default function OrdersPage() {
  const router = useRouter()
  const { user, isLoading, isAuthenticated } = useAuth()
  const [orders, setOrders] = useState<OrderSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.replace('/login?redirect=/orders')
    }
  }, [isLoading, isAuthenticated, router])

  useEffect(() => {
    if (!isAuthenticated) return
    getMyOrders()
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  if (isLoading || !isAuthenticated) {
    return (
      <ShopLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-neutral-500">Laddar…</div>
      </ShopLayout>
    )
  }

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Account header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Mitt konto</h1>
            <p className="text-sm text-neutral-500 mt-1">{user?.email}</p>
          </div>
          <Link href="/profile" className="px-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700">
            Profil
          </Link>
        </div>

        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Mina beställningar</h2>

        {loading ? (
          <div className="py-16 text-center text-neutral-500">Laddar…</div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-12 text-center">
            <p className="text-neutral-500">Du har inga beställningar ännu.</p>
            <Link href="/" className="inline-block mt-4 px-6 py-3 rounded-xl bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium">
              Börja handla
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <Link
                key={order.id}
                href={`/orders/${order.id}`}
                className="flex items-center justify-between gap-4 p-5 rounded-2xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:shadow-md transition-shadow"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-neutral-900 dark:text-white">#{order.order_number}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[order.status] ?? STATUS_STYLES.cancelled}`}>
                      {orderStatusLabel(order.status)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-1">
                    {formatDate(order.created_at)} · {order.item_count} {order.item_count === 1 ? 'artikel' : 'artiklar'}
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-semibold text-neutral-900 dark:text-white">{formatPrice(order.total_amount)}</span>
                  <svg className="w-5 h-5 text-neutral-400 inline-block ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ShopLayout>
  )
}
